import phone from 'phone';
import { IdentificationType } from 'policyholder/domain/policyholder-identification';
import { ProductModule } from 'product-modules/domain/product-module';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Col, Row } from 'reactstrap';
import { SteppedComponentsBehavior } from 'rootstrap/components-old/root-schema-form/utils/stepped-components-behavior';
import { ValidationTypes } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { CardGroup } from 'rootstrap/components/card';
import { BCP47ToIsoCountryCode } from 'rootstrap/components/forms/countries';
import {
  InputField,
  InputFieldDisplayProperties,
  PrefillAction,
} from 'rootstrap/components/forms/new-fields/input-field';
import { PhoneNumberField } from 'rootstrap/components/forms/new-fields/phone-number-field';
import { ActiveElement } from 'rootstrap/components/forms/new-fields/utils';
import { usePromise } from 'shared/hooks/promise';
import {
  EmbeddedConfigSection,
  getDisplayOptionalSections,
  getWording,
  ProductModuleEmbedPersonalDetailsContactDetailTypes,
  isReadOnly,
} from 'site-config';
import { useSiteConfigContext } from 'style-context';
import { isPrefilledFromLookupData } from '../utils/helpers';
import { PersonalDetailsIdentificationData } from './identification';
import { PersonalDetailsInputs, PolicyholderPrefillValues } from './personal-details';
import { PolicyholderLookUpFormData } from './unauthenticated-policyholder-lookup';
import { getInputFieldConfig } from '../utils/helpers';

export interface ContactDetailCellphone {
  countryCode: string;
  number: string;
}
export interface ContactDetailsData {
  email?: string;
  cellphone?: ContactDetailCellphone;
}
interface Params {
  setIsValid: (contactDetailsIsValid: boolean) => void;
  setSectionData: (contactDetailsSectionData: ContactDetailsData) => void;
  contactDetailsSectionData: ContactDetailsData | undefined;
  prefillValues: PolicyholderPrefillValues;
  setActiveElement: (params: ActiveElement) => void;
  activeElement: ActiveElement;
  isCompleted: boolean;
  policyholderLookupFormData: PolicyholderLookUpFormData | undefined;
  disableSteppedComponent?: boolean;
  productModule?: ProductModule;
  identificationSectionData?: PersonalDetailsIdentificationData;
  isManagementContext: boolean | undefined;
}

export const ContactDetailsSection = (params: Params) => {
  const {
    setIsValid,
    setSectionData,
    prefillValues,
    contactDetailsSectionData,
    setActiveElement,
    activeElement,
    isCompleted,
    policyholderLookupFormData,
    productModule,
    identificationSectionData,
  } = params;

  const { siteConfig } = useSiteConfigContext();

  const fetchifyAutoCompleteEnabled =
    siteConfig?.[EmbeddedConfigSection.PersonalDetails].displayOptionalSections.fetchifyAutoComplete;
  const defaultCountryCodeFromBrowser = siteConfig?.settings?.defaultCountryCodeFromBrowser;

  const disableSteppedComponents =
    !!siteConfig?.styles.disableSteppedComponents || Boolean(params.disableSteppedComponent);

  const steppedComponentsBehavior: SteppedComponentsBehavior = {
    disableNextButton: disableSteppedComponents,
    hideDivider: disableSteppedComponents,
    disableScrollToElement: disableSteppedComponents,
    disableActiveElement: disableSteppedComponents,
    isTouched: !disableSteppedComponents ? isCompleted : true,
  };
  const isPersonalDetailsReadOnly = isReadOnly(params.isManagementContext, siteConfig, 'personalDetails');

  const displayCellphoneNumber = getDisplayOptionalSections({
    displayOptionalSection: siteConfig?.personalDetails.displayOptionalSections.contactDetailTypes.includes(
      ProductModuleEmbedPersonalDetailsContactDetailTypes.Cellphone,
    ),
  });

  const displayEmailAddress = getDisplayOptionalSections({
    displayOptionalSection: siteConfig?.personalDetails.displayOptionalSections.contactDetailTypes.includes(
      ProductModuleEmbedPersonalDetailsContactDetailTypes.Email,
    ),
  });

  // Check if email should be required
  const selectedIdType = identificationSectionData?.idType;
  const isEmailRequiredByProductModule =
    productModule?.productModuleDefinition?.settings.policyholder.individualPolicyholderFields?.email?.required ?? true;

  const isEmailIdType = selectedIdType === IdentificationType.Email;
  const isEmailRequired = isEmailRequiredByProductModule || isEmailIdType;

  const form = useForm<Partial<FormData>>({
    mode: 'onChange',
    defaultValues: useMemo(
      () => ({
        values: { ...contactDetailsSectionData },
      }),
      [],
    ),
  });
  form.watch();

  // Trigger validation when ID type changes
  useEffect(() => {
    form.trigger();
  }, [identificationSectionData?.idType]);

  usePromise(async () => {
    execute();
    await form.trigger();
  }, []);

  usePromise(async () => {
    setIsValid(form.formState.isValid);
  }, [form.formState.isValid]);

  const execute = () => {
    const formValues = form.getValues() as ContactDetailsData;
    setSectionData(formValues);
  };

  useEffect(execute, []);

  return (
    <CardGroup>
      <form
        onBlur={() => execute()}
        onChange={() => execute()}
        onSubmit={form.handleSubmit((data: ContactDetailsData) => setSectionData(data))}
      >
        {displayEmailAddress && (
          <Row>
            <Col sm={12}>
              <InputField
                placeholder={undefined}
                defaultValue={
                  isPrefilledFromLookupData(policyholderLookupFormData, IdentificationType.Email)
                    ? policyholderLookupFormData?.email
                    : contactDetailsSectionData?.email
                }
                validators={
                  isEmailRequired
                    ? [
                        {
                          validation: {
                            type: ValidationTypes.REQUIRED,
                          },
                        },
                        {
                          validation: {
                            type: ValidationTypes.EMAIL,
                          },
                        },
                      ]
                    : [
                        {
                          validation: {
                            type: ValidationTypes.EMAIL,
                          },
                        },
                      ]
                }
                name={PersonalDetailsInputs.Email}
                label={getWording({
                  wording: getInputFieldConfig('email', params.isManagementContext, siteConfig)?.label,
                })}
                prefillAction={
                  isPrefilledFromLookupData(policyholderLookupFormData, IdentificationType.Email)
                    ? PrefillAction.Disabled
                    : getInputFieldConfig('email', params.isManagementContext, siteConfig)?.prefillAction
                }
                form={form}
                prefillValue={
                  isPrefilledFromLookupData(policyholderLookupFormData, IdentificationType.Email)
                    ? policyholderLookupFormData?.email
                    : prefillValues.email
                }
                isDisabled={isPersonalDetailsReadOnly}
                displayProperties={
                  {
                    activeElement,
                    setActiveElement,
                    nextComponentName: displayCellphoneNumber
                      ? PersonalDetailsInputs.Cellphone
                      : PersonalDetailsInputs.AddressOptIn,
                  } as InputFieldDisplayProperties
                }
                {...steppedComponentsBehavior}
              />
            </Col>
          </Row>
        )}
        {displayCellphoneNumber && (
          <Row>
            <Col sm={12}>
              <PhoneNumberField
                placeholder={undefined}
                defaultValue={
                  isPrefilledFromLookupData(policyholderLookupFormData, IdentificationType.Cellphone)
                    ? policyholderLookupFormData?.cellphone
                    : defaultValues({
                        contactDetailsSectionData,
                        defaultCountryCodeFromBrowser,
                      })
                }
                validators={[
                  {
                    validation: {
                      type: ValidationTypes.REQUIRED,
                    },
                  },
                ]}
                name={PersonalDetailsInputs.Cellphone}
                label={getWording({
                  wording: getInputFieldConfig('cellphone', params.isManagementContext, siteConfig)?.label,
                })}
                prefillAction={
                  isPrefilledFromLookupData(policyholderLookupFormData, IdentificationType.Cellphone)
                    ? PrefillAction.Disabled
                    : getInputFieldConfig('cellphone', params.isManagementContext, siteConfig)?.prefillAction
                }
                form={form}
                prefillValue={
                  isPrefilledFromLookupData(policyholderLookupFormData, IdentificationType.Cellphone)
                    ? policyholderLookupFormData?.cellphone
                    : prefillValues.cellphone
                }
                isDisabled={isPersonalDetailsReadOnly}
                displayProperties={
                  {
                    activeElement,
                    setActiveElement,
                    nextComponentName: fetchifyAutoCompleteEnabled
                      ? PersonalDetailsInputs.FetchifyAutocomplete
                      : PersonalDetailsInputs.AddressOptIn,
                  } as InputFieldDisplayProperties
                }
                {...steppedComponentsBehavior}
              />
            </Col>
          </Row>
        )}
      </form>
    </CardGroup>
  );
};

const defaultValues = (params: {
  contactDetailsSectionData: ContactDetailsData | undefined;
  defaultCountryCodeFromBrowser: boolean | undefined;
}) => {
  const { contactDetailsSectionData, defaultCountryCodeFromBrowser } = params;

  return {
    countryCode:
      contactDetailsSectionData?.cellphone?.countryCode ||
      phone(contactDetailsSectionData?.cellphone?.number || '').countryIso2 ||
      (defaultCountryCodeFromBrowser && BCP47ToIsoCountryCode()) ||
      'ZA',
    number: contactDetailsSectionData?.cellphone?.number?.replace(
      phone(contactDetailsSectionData?.cellphone?.number || '').countryCode || '',
      '',
    ),
  };
};
